import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MapPin, Calendar, Plus, User, Edit, LogOut, AlertCircle, CheckCircle, Mail, QrCode, HelpCircle } from 'lucide-react';
import GoogleMap from '../components/GoogleMap';
import FestivalForm from '../components/FestivalForm';
import UserLoginStatusInfolet from '../components/UserLoginStatusInfolet';

const UserDashboard = ({ view = 'festivals-nearby' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Check if user has unverified role
  const isUnverified = user?.roles?.includes('unverified');

  const handleLogout = async () => {
    await logout();
    // Full page redirect to landing page (not React Router navigation)
    window.location.href = '/';
  };

  const menuItems = [
    { id: 'how-it-works', label: 'How It Works', icon: HelpCircle, path: '/user' },
    { id: 'festivals-nearby', label: 'Festivals Nearby', icon: MapPin, path: '/user/festivals-nearby' },
    { id: 'your-festivals', label: 'Your Festivals', icon: Calendar, path: '/user/festivals' },
    { id: 'create-festival', label: 'Create New Festival', icon: Plus, path: '/user/create' },
    { id: 'account', label: 'Account', icon: User, path: '/user/account', showWarning: isUnverified },
    { id: 'qr-code', label: 'Show QR Code', icon: QrCode, path: '/user/qr-code' },
  ];

  // Floating card style
  const floatingCardStyle = {
    background: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'url("/DesktopLandingPageBG.jpg") center/cover no-repeat fixed',
      position: 'relative'
    }}>
      {/* Background overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(255, 255, 255, 0.3)',
        zIndex: 0
      }} />

      {/* Status Bar - Floating Card */}
      <header style={{
        ...floatingCardStyle,
        margin: '15px 15px 0 15px',
        padding: '0 20px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 10,
        position: 'relative'
      }}>
        <h1
          onClick={() => navigate('/user')}
          style={{ fontSize: '24px', color: '#333', cursor: 'pointer' }}
        >
          Festivaleer
        </h1>
        <UserLoginStatusInfolet />
      </header>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', padding: '15px', gap: '15px', zIndex: 1 }}>
        {/* Left Menu - Floating Card */}
        <nav style={{
          ...floatingCardStyle,
          width: '250px',
          padding: '20px',
          overflowY: 'auto'
        }}>
          <ul style={{ listStyle: 'none' }}>
            {menuItems.map(item => {
              const Icon = item.icon;
              const isActive = view === item.id;
              return (
                <li key={item.id} style={{ marginBottom: '10px' }}>
                  <button
                    onClick={() => navigate(item.path)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      textDecoration: 'none',
                      color: isActive ? '#007bff' : '#666',
                      backgroundColor: isActive ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Icon size={20} />
                    {item.label}
                    {item.showWarning && (
                      <AlertCircle size={18} style={{ color: '#f59e0b', marginLeft: 'auto' }} title="Email not verified" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Main Content - Floating Card (or side-by-side cards for how-it-works) */}
        {view === 'how-it-works' ? (
          <HowItWorksView floatingCardStyle={floatingCardStyle} />
        ) : (
          <div style={{
            ...floatingCardStyle,
            flex: 1,
            padding: '20px',
            overflow: view === 'festivals-nearby' ? 'hidden' : 'auto'
          }}>
            {view === 'festivals-nearby' && <FestivalsNearbyView navigate={navigate} />}
            {view === 'your-festivals' && <YourFestivalsView navigate={navigate} />}
            {view === 'create-festival' && <CreateFestivalView navigate={navigate} />}
            {view === 'account' && <AccountView user={user} onLogout={handleLogout} />}
            {view === 'qr-code' && <QRCodeView />}
          </div>
        )}
      </div>
    </div>
  );
};

// Festivals Nearby View
const FestivalsNearbyView = ({ navigate }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [selectedFestival, setSelectedFestival] = useState(null);

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setUserLocation({ lat: 59.33, lng: 18.07 }) // Default to Stockholm
      );
    }
  }, []);

  const { data: festivalsData } = useQuery('festivals', async () => {
    const response = await axios.get('/api/festivals?status=active');
    return response.data;
  });

  const festivals = festivalsData?.festivals || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <h2 style={{ marginBottom: '20px', flexShrink: 0 }}>Festivals Nearby</h2>
      <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', minHeight: 0 }}>
        <GoogleMap
          center={userLocation || { lat: 59.33, lng: 18.07 }}
          zoom={10}
          markers={festivals.map(f => ({
            id: f.id,
            lat: f.location_lat,
            lng: f.location_lng,
            title: f.name
          }))}
          onMarkerClick={(marker) => setSelectedFestival(festivals.find(f => f.id === marker.id))}
          showSearch={false}
          height="100%"
        />
      </div>
      {selectedFestival && (
        <FestivalPopupCard
          festival={selectedFestival}
          onClose={() => setSelectedFestival(null)}
          onView={() => navigate(`/mweb?festival=${selectedFestival.id}`)}
          onEdit={() => navigate(`/user/festivals/${selectedFestival.id}/editor`)}
        />
      )}
    </div>
  );
};

// Festival Popup Card
const FestivalPopupCard = ({ festival, onClose, onView, onEdit }) => (
  <div style={{
    position: 'fixed',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    zIndex: 1000,
    minWidth: '300px'
  }}>
    <button onClick={onClose} style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
    <h3 style={{ marginBottom: '10px' }}>{festival.name}</h3>
    <p style={{ color: '#666', marginBottom: '15px' }}>{festival.description || 'No description'}</p>
    <div style={{ display: 'flex', gap: '10px' }}>
      <button onClick={onView} className="btn btn-primary">View</button>
      {festival.canEdit && <button onClick={onEdit} className="btn btn-secondary"><Edit size={16} /> Edit</button>}
    </div>
  </div>
);

// Your Festivals View
const YourFestivalsView = ({ navigate }) => {
  const { data: festivalsData, isLoading } = useQuery('user-festivals', async () => {
    const response = await axios.get('/api/festivals?mine=true');
    return response.data;
  });

  const festivals = festivalsData?.festivals || [];

  if (isLoading) {
    return <div className="loading">Loading your festivals...</div>;
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>Your Festivals</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
        {festivals.map(festival => (
          <div
            key={festival.id}
            onClick={() => navigate(`/user/festivals/${festival.id}/editor`)}
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
          >
            <h3 style={{ marginBottom: '10px', color: '#333' }}>{festival.name}</h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>{festival.description || 'No description'}</p>
            <span style={{
              display: 'inline-block',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              background: festival.status === 'published' ? '#d4edda' : '#fff3cd',
              color: festival.status === 'published' ? '#155724' : '#856404'
            }}>
              {festival.status}
            </span>
          </div>
        ))}
        {/* Create New Festival Card */}
        <div
          onClick={() => navigate('/user/create')}
          style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '150px',
            border: '2px dashed #ddd',
            transition: 'border-color 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#007bff'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#ddd'}
        >
          <Plus size={48} color="#007bff" />
          <p style={{ marginTop: '10px', color: '#007bff', fontWeight: '500' }}>Create New Festival</p>
        </div>
      </div>
    </div>
  );
};

// Create Festival View
const CreateFestivalView = ({ navigate }) => {
  const queryClient = useQueryClient();

  const createMutation = useMutation(
    async (data) => {
      const response = await axios.post('/api/festivals', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('user-festivals');
        toast.success('Festival created!');
        navigate(`/user/festivals/${data.festival.id}/editor`);
      },
      onError: () => toast.error('Failed to create festival')
    }
  );

  const handleSubmit = (formData) => {
    createMutation.mutate(formData);
  };

  const handleCancel = () => {
    navigate('/user');
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>Create New Festival</h2>
      <FestivalForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={createMutation.isLoading}
        submitLabel="Create Festival"
        showStatus={false}
      />
    </div>
  );
};

// Account View
const AccountView = ({ user, onLogout }) => {
  const [sendingVerification, setSendingVerification] = useState(false);
  const isUnverified = user?.roles?.includes('unverified');

  const handleResendVerification = async () => {
    setSendingVerification(true);
    try {
      await axios.post('/api/auth/send-verification-email');
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send verification email');
    } finally {
      setSendingVerification(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>Account</h2>
      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '400px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: '#666', fontSize: '14px', marginBottom: '5px' }}>Name</label>
          <p style={{ fontSize: '18px', color: '#333' }}>{user?.name || 'Unknown'}</p>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: '#666', fontSize: '14px', marginBottom: '5px' }}>Email</label>
          <p style={{ fontSize: '18px', color: '#333' }}>{user?.email || 'Unknown'}</p>
        </div>

        {/* Email Verification Status */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ display: 'block', color: '#666', fontSize: '14px', marginBottom: '5px' }}>Email Status</label>
          {isUnverified ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <AlertCircle size={20} style={{ color: '#d97706' }} />
              <div style={{ flex: 1 }}>
                <p style={{ color: '#92400e', fontWeight: '500', margin: 0 }}>Email not verified</p>
                <p style={{ color: '#a16207', fontSize: '13px', margin: '4px 0 0 0' }}>
                  Please verify your email to publish festivals.
                </p>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: '#d1fae5',
              borderRadius: '8px',
              border: '1px solid #10b981'
            }}>
              <CheckCircle size={20} style={{ color: '#059669' }} />
              <p style={{ color: '#065f46', fontWeight: '500', margin: 0 }}>Email verified</p>
            </div>
          )}
        </div>

        {/* Resend Verification Button */}
        {isUnverified && (
          <button
            onClick={handleResendVerification}
            disabled={sendingVerification}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', width: '100%' }}
          >
            <Mail size={16} />
            {sendingVerification ? 'Sending...' : 'Resend Verification Email'}
          </button>
        )}

        <button onClick={onLogout} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LogOut size={16} />
          Log Out
        </button>
      </div>
    </div>
  );
};

// QR Code View
const QRCodeView = () => {
  const [qrUrl, setQrUrl] = useState('Loading...');

  useEffect(() => {
    // Read the URL from the QR code image metadata
    async function loadQRUrl() {
      try {
        const response = await fetch('/qr-code.png');
        const buffer = await response.arrayBuffer();
        const data = new Uint8Array(buffer);

        // Parse PNG to find tEXt chunks
        let offset = 8; // PNG signature is 8 bytes
        while (offset < data.length) {
          const length = (data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3];
          const type = String.fromCharCode(data[offset + 4], data[offset + 5], data[offset + 6], data[offset + 7]);

          if (type === 'tEXt') {
            const chunkData = data.slice(offset + 8, offset + 8 + length);
            let nullIndex = 0;
            while (nullIndex < chunkData.length && chunkData[nullIndex] !== 0) nullIndex++;
            const keyword = String.fromCharCode(...chunkData.slice(0, nullIndex));
            const text = String.fromCharCode(...chunkData.slice(nullIndex + 1));

            if (keyword === 'QR-URL') {
              setQrUrl(text);
              return;
            }
          }

          if (type === 'IEND') break;
          offset += 12 + length;
        }

        setQrUrl('festivaleer.com');
      } catch (error) {
        console.error('Failed to read QR metadata:', error);
        setQrUrl('festivaleer.com');
      }
    }
    loadQRUrl();
  }, []);

  return (
    <div>
      <h2 style={{ marginBottom: '30px' }}>QR Code</h2>
      <div style={{
        display: 'flex',
        gap: '60px',
        alignItems: 'flex-start',
        flexWrap: 'wrap'
      }}>
        {/* Left column - QR Code */}
        <div style={{
          flex: '0 0 300px',
          textAlign: 'center'
        }}>
          <img
            src="/qr-code.png"
            alt="Festivaleer QR Code"
            style={{
              width: '100%',
              maxWidth: '300px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}
          />
          <p style={{
            marginTop: '15px',
            fontSize: '1.1rem',
            color: '#667eea',
            fontWeight: '500'
          }}>
            {qrUrl}
          </p>
        </div>

        {/* Right column - Text */}
        <div style={{ flex: 1, minWidth: '280px' }}>
          <h3 style={{
            fontSize: '1.5rem',
            color: '#667eea',
            marginBottom: '20px'
          }}>
            Share Your Event
          </h3>
          <p style={{
            color: '#555',
            marginBottom: '15px',
            lineHeight: '1.7'
          }}>
            Put this QR code on your printed materials—flyers, posters, programs, or signage—and attendees can instantly access navigation for your event.
          </p>
          <p style={{
            color: '#555',
            marginBottom: '15px',
            lineHeight: '1.7'
          }}>
            The QR code simply links to Festivaleer. When visitors scan it and open the page, their location is automatically detected to show them relevant events nearby.
          </p>
          <div style={{
            background: '#f0f4ff',
            padding: '15px 20px',
            borderRadius: '8px',
            borderLeft: '4px solid #667eea',
            marginTop: '20px'
          }}>
            <p style={{
              margin: 0,
              color: '#444',
              lineHeight: '1.7'
            }}>
              <strong>Pro tip:</strong> You don't need a special QR code for each event. This single QR code works for all festivals—attendees are automatically shown events based on their location!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// How It Works View - Two side-by-side floating cards
const HowItWorksView = ({ floatingCardStyle }) => {
  return (
    <div style={{
      display: 'flex',
      gap: '20px',
      flex: 1,
      overflow: 'auto',
      alignItems: 'flex-start'
    }}>
      {/* Left Card - Create */}
      <div style={{
        ...floatingCardStyle,
        flex: 1,
        padding: '30px'
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          color: '#667eea',
          marginBottom: '20px'
        }}>
          Create
        </h2>
        <p style={{
          color: '#555',
          lineHeight: '1.7',
          marginBottom: '25px',
          fontSize: '1rem'
        }}>
          Anyone can create a festival or event on Festivaleer. Whether you're organizing a music festival,
          a local fair, a conference, or any gathering—simply add your event, mark the points of interest
          on the map, and you're ready to help your attendees navigate.
        </p>
        <img
          src="/Laptop_Cafe_Good.jpg"
          alt="Creating a festival on Festivaleer"
          style={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            objectFit: 'cover'
          }}
        />
      </div>

      {/* Middle Card - Market */}
      <div style={{
        ...floatingCardStyle,
        flex: 1,
        padding: '30px'
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          color: '#667eea',
          marginBottom: '20px'
        }}>
          Market
        </h2>
        <p style={{
          color: '#555',
          lineHeight: '1.7',
          marginBottom: '25px',
          fontSize: '1rem'
        }}>
          Print our QR code on flyers, posters, programs, or signage around your event. When attendees
          scan it, they're instantly connected to Festivaleer and can start navigating—no app download
          required. One QR code works for all events based on the visitor's location.
        </p>
        <img
          src="/Scanning_the_QR_code.jpg"
          alt="Scanning the Festivaleer QR code"
          style={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            objectFit: 'cover'
          }}
        />
      </div>

      {/* Right Card - Attend */}
      <div style={{
        ...floatingCardStyle,
        flex: 1,
        padding: '30px'
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          color: '#667eea',
          marginBottom: '20px'
        }}>
          Attend
        </h2>
        <p style={{
          color: '#555',
          lineHeight: '1.7',
          marginBottom: '25px',
          fontSize: '1rem'
        }}>
          As an attendee, Festivaleer helps you find your way around any event. Point your phone toward
          stages, food vendors, restrooms, or any point of interest—the compass arrows guide you there.
          You can even find your friends in the crowd using the Friend Finder feature.
        </p>
        <img
          src="/Friends_Navigating.jpg"
          alt="Friends navigating at a festival"
          style={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            objectFit: 'cover'
          }}
        />
      </div>
    </div>
  );
};

export default UserDashboard;

